import React, { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../hooks';
import { Button, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import RepeatIcon from '@mui/icons-material/Repeat';
import IosShareIcon from '@mui/icons-material/IosShare';
import { notify } from '../reducers/notificationReducer';
import { sharePost } from '../utils/sharePost';
import getErrorMsg from '../utils/getErrorMsg';

interface ShareButtonProps {
  id: string;
  title: string;
  pillSx: Record<string, unknown>;
  size?: 'small' | 'medium';
}

/**
 * Share menu: "Share to a herd" sends the user to the create page with the
 * original's in-app link prefilled in the description (that link is what makes it
 * a share — no separate dialog). "Copy link / share" is the external OS share.
 */
const ShareButton: FC<ShareButtonProps> = ({ id, title, pillSx, size = 'medium' }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const closeMenu = () => setAnchorEl(null);

  const shareToHerd = () => {
    closeMenu();
    navigate(`/submit?repost=${id}`);
  };

  const shareExternally = async () => {
    closeMenu();
    try {
      await sharePost({ id, title });
    } catch (err) {
      dispatch(notify(getErrorMsg(err), 'error'));
    }
  };

  return (
    <>
      <Button
        startIcon={<ShareIcon />}
        sx={pillSx}
        size={size}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-haspopup="true"
      >
        Share
      </Button>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
        <MenuItem onClick={shareToHerd}>
          <ListItemIcon><RepeatIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Share to a herd</ListItemText>
        </MenuItem>
        <MenuItem onClick={shareExternally}>
          <ListItemIcon><IosShareIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Copy link / share</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default ShareButton;
